import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Zap, Users, Shield, TrendingUp, Cpu, Globe } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import SEO from '../components/SEO';


const SEO_KEYS = {
    'ai-staffing': 'solutions_ai',
    'cost-optimization': 'solutions_cost',
    'offshore-strategy': 'solutions_strategy',
    'digital-transformation': 'solutions_dt'
};

const servicesData = {
    'ai-staffing': {
        title: "AI-Augmented Staffing Solutions | Elite Talent + Advanced Automation",
        metaDesc: "Revolutionize your workforce with AI-augmented staffing. Combine top 1% Filipino talent with cutting-edge AI tools to boost productivity by 200% and cut costs by 70%.",
        hero: {
            title: "AI-Augmented Staffing: The Future of Offshore Teams",
            subtitle: "Don't just hire people. Hire a super-powered workforce equipped with the latest AI tools to deliver 2x the output at half the cost.",
            image: "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "The Old BPO Model is Dead. Welcome to Staffing 2.0.",
                text: "For decades, the BPO industry has sold you on one thing: cheap labor. You hire a remote employee, they do the work, you save some money. But in 2026, 'cheap' isn't enough. You need *efficient*. You need *innovative*. You need *speed*.\n\nTraditional offshoring is plagued by inefficiencies. Manual data entry, repetitive tasks, and communication gaps slow down your operations. AI-Augmented Staffing changes the game by equipping elite offshore talent with a custom stack of AI tools—Generative AI, Automation Agents, and Intelligent Analytics—empowering one person to do the work of three."
            },
            {
                type: 'benefits',
                heading: "Why AI-Augmented Staffing?",
                items: [
                    { icon: <Zap size={24} />, title: "200% Productivity Boost", desc: "Our staff use AI co-pilots for coding, writing, and data analysis, drastically reducing turnaround times." },
                    { icon: <TrendingUp size={24} />, title: "70% Cost Reduction", desc: "Save on salaries AND software licenses. We provide the talent and the tech stack integration." },
                    { icon: <Shield size={24} />, title: "Zero Hallucination Policy", desc: "We train our staff on 'Human-in-the-Loop' protocols, ensuring AI output is verified for accuracy and compliance." },
                    { icon: <Cpu size={24} />, title: "Custom AI Tooling", desc: "We don't just give them ChatGPT. We build custom workflows using tools like Zapier, Make, and proprietary agents." }
                ]
            },
            {
                type: 'deepDive',
                heading: "How We Integrate AI into Every Role",
                sections: [
                    {
                        title: "Customer Support & CX",
                        content: "Forget scripted robots. Our CX agents use AI to analyze customer sentiment in real-time, suggest hyper-personalized responses, and automate ticket tagging. This results in a 40% reduction in average handling time (AHT) and a CSAT score that rivals onshore teams."
                    },
                    {
                        title: "Virtual Assistants & Admin",
                        content: "A traditional VA manages your calendar. An AI-Augmented VA automates your entire inbox, drafts complex reports using predictive text, and manages data entry with OCR technology. They don't just follow instructions; they optimize workflows."
                    },
                    {
                        title: "Digital Marketing & Content",
                        content: "Our content creators harness LLMs to generate high-quality drafts, which they then refine with human creativity and cultural nuance. They use AI for SEO analysis, competitor tracking, and automated social media scheduling, delivering the output of a full agency."
                    },
                    {
                        title: "Software Development",
                        content: "Our developers are pair-programmed with AI coding assistants (GitHub Copilot, Cursor). This leads to faster code generation, automated unit testing, and cleaner documentation. You get robust, scalable code in a fraction of the time."
                    }
                ]
            },
            {
                type: 'education',
                heading: "The 'Human-in-the-Loop' Advantage",
                text: "Many business owners fear that AI will replace quality with generic, error-prone output. We solve this with our rigorous 'Human-in-the-Loop' (HITL) methodology.\n\nAI generates the draft, the code, or the data. The Human—your dedicated BPO Consultant—verifies, refines, and finalizes it. This hybrid approach ensures you get the speed of automation with the judgment, empathy, and creativity of a top-tier professional. It allows your offshore team to punch above their weight class, delivering results that formerly required expensive senior hires."
            },
            {
                type: 'cta',
                heading: "Ready to Supercharge Your Workforce?",
                text: "Stop hiring task-doers. Start hiring AI-enabled problem solvers. Let's build your custom AI-augmented team today.",
                button: "Get Your Free AI Staffing Roadmap"
            }
        ]
    },
    'cost-optimization': {
        title: "Strategic Cost Optimization | Save 70% Without Sacrificing Quality",
        metaDesc: "Maximize your runway with strategic BPO cost optimization. We don't just cut costs; we optimize value through labor arbitrage, process efficiency, and tax incentives.",
        hero: {
            title: "Smart Cost Optimization: Stop Bleeding Cash",
            subtitle: "Turn your biggest expense (payroll) into your biggest competitive advantage using Global Labor Arbitrage.",
            image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "The 'Race to the Bottom' is a Trap",
                text: "Most companies approach cost cutting the wrong way. They hire the cheapest freelancers they can find, only to pay double in management time, errors, and rework. That isn't optimization; it's self-sabotage.\n\nTrue Cost Optimization isn't about paying people less—it's about paying the *right* price in the *right* market for the *best* talent. By leveraging the Philippines' economic structure, you can hire a Senior Data Analyst for the price of a Junior Intern in the US, while actually offering them a top-tier local salary. This creates loyalty, quality, and massive savings simultaneously."
            },
            {
                type: 'benefits',
                heading: "Where We Find the Money",
                items: [
                    { icon: <TrendingUp size={24} />, title: "Labor Arbitrage", desc: "Access senior-level talent at 30% of Western market rates due to cost-of-living differences." },
                    { icon: <Zap size={24} />, title: "Operational Efficiency", desc: "Streamline workflows to remove bottlenecks, reducing the 'time-cost' of every task." },
                    { icon: <Shield size={24} />, title: "Infrastructure Savings", desc: "No need for local office space, equipment, or benefits packages. We handle the overhead." },
                    { icon: <Cpu size={24} />, title: "Tech Stack Consolidation", desc: "We audit your software licenses and replace expensive enterprise seats with efficient team accounts." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Our 3-Phase Optimization Framework",
                sections: [
                    {
                        title: "Phase 1: The 'Waste Audit'",
                        content: "We don't just look at salaries. We analyze your entire operational workflow. We identify tasks that are highly repetitive, low-value for your core team, but high-cost in terms of time. These are the first targets for offshoring or automation."
                    },
                    {
                        title: "Phase 2: The 'Plug-and-Play' Transfer",
                        content: "We document your processes into Standard Operating Procedures (SOPs). This ensures that when we move a function offshore, knowledge isn't lost. We create a 'Digital Twin' of your local workflow, ensuring the offshore team steps into a structured environment, not a chaotic one."
                    },
                    {
                        title: "Phase 3: Continuous Improvement Loop",
                        content: "Cost optimization isn't a one-time event. Our teams are incentivized to find faster ways to do their jobs. We implement quarterly reviews where we re-assess tools and workflows, often finding an additional 10-15% efficiency gain year-over-year."
                    }
                ]
            },
            {
                type: 'education',
                heading: "The ROI of 'Quality' Over 'Cheap'",
                text: "Let's do the math. \n\n**Option A (Cheap):** Hire a $5/hr freelancer. They require 10 hours of your management/week. They make errors that delay launch by 2 weeks. Total Cost: Low salary, but massive Opportunity Cost.\n\n**Option B (Strategic):** Hire a $12/hr Senior Offshore Pro. They require 1 hour of management/week. They improve your process. You launch on time. \n\nWe focus exclusively on Option B. We target the top 10% of the talent pool, pay them above-market rates to ensure 98% retention, and STILL save you 60-70% compared to onshore hires. This is the difference between 'outsourcing' and 'optimizing'."
            },
            {
                type: 'cta',
                heading: "Calculate Your Potential Savings",
                text: "Don't guess. Let us run a full comprehensive audit of your current roles and show you exactly how much capital you can free up for reinvestment.",
                button: "Request Free Cost Audit"
            }
        ]
    },
    'offshore-strategy': {
        title: "Offshore Strategy Consulting | Build Your Global Org Chart",
        metaDesc: "Offshoring fails without a strategy. We design your global organization chart, ensuring cultural alignment, legal compliance, and operational scalability.",
        hero: {
            title: "Offshore Strategy: Blueprint for Expansion",
            subtitle: "Stop treating offshoring like a gig. Start treating it like an acquisition. We build your satellite office in the cloud.",
            image: "https://images.unsplash.com/photo-1526304640152-d4619684e884?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "You Don't Need 'Helpers'. You Need a Department.",
                text: "The biggest mistake business owners make is hiring one-off virtual assistants to 'put out fires.' This creates a fragmented, chaotic team structure giving you more people to manage, not less.\n\nOur Offshore Strategy service builds a cohesive extension of your company. We design an org chart with team leads, QA specialists, and individual contributors. We establish reporting lines, KPIs, and cultural rituals that make your offshore team feel just as part of the vision as your local team."
            },
            {
                type: 'benefits',
                heading: "Strategic Pillars",
                items: [
                    { icon: <Globe size={24} />, title: "Cultural Integration", desc: "We bridge the gap between Western business expectations and Filipino service culture." },
                    { icon: <Users size={24} />, title: "Org Chart Design", desc: "We structure your team with clear hierarchy, career paths, and succession planning." },
                    { icon: <Shield size={24} />, title: "Risk Mitigation", desc: "We handle data privacy (GDPR/Data Privacy Act), contracts, and redundant internet/power." },
                    { icon: <Zap size={24} />, title: "Knowledge Management", desc: "We build your internal Wiki/Knowledge Base so your business IP is documented and secure." }
                ]
            },
            {
                type: 'deepDive',
                heading: "The 'Satellite Office' Mindset",
                sections: [
                    {
                        title: "Step 1: Role Decomposition",
                        content: "We analyze your onshore senior staff. What are they spending their time on that isn't their 'Zone of Genius'? We strip those tasks away to create clear, high-impact offshore job descriptions."
                    },
                    {
                        title: "Step 2: The 'Bridge' Hire",
                        content: "We often recommend starting with a Team Lead or 'Bridge' Manager. This person acts as the conduit between your local leadership and the broader offshore team, ensuring you have a single point of accountability."
                    },
                    {
                        title: "Step 3: The Operational Rhythm",
                        content: "We install your meeting cadence. Daily standups, weekly scorecards, monthly retrospectives. We ensure the offshore team runs on *your* heartbeat, not in a silo."
                    }
                ]
            },
            {
                type: 'education',
                heading: "Why Culture Eats Strategy for Breakfast",
                text: "In the Philippines, 'Malasakit' (genuine care/concern) is a core cultural value. If you treat your offshore team like transactional vendors, you get transactional results. \n\nOur strategy focuses heavily on unlocking 'Malasakit'. We teach you how to onboard, how to give feedback, and how to recognize wins in a way that resonates culturally. When you get this right, you don't just get employees; you get brand evangelists who will work tirelessly for your success. We provide the playbook for this cultural calibration."
            },
            {
                type: 'cta',
                heading: "Design Your Global Team",
                text: "Ready to move beyond 'hiring a VA' and start building a scalable organization? Let's sketch out your future org chart.",
                button: "Book Strategy Session"
            }
        ]
    },
    'digital-transformation': {
        title: "Digital Transformation | Modernize Before You Scale",
        metaDesc: "Don't offshore a mess. We modernize your legacy workflows with low-code automation and cloud integration before we build your team.",
        hero: {
            title: "Digital Transformation",
            subtitle: "Fix your broken processes before you scale them. We digitize your workflow to make it offshore-ready.",
            image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "Automate First, Delegate Second",
                text: "Many businesses try to solve efficiency problems by throwing more people at them. If your process is broken, hiring more people just makes the chaos faster.\n\nOur Digital Transformation service focuses on 'Pre-Offshoring Hygiene'. We audit your tech stack, kill legacy spreadsheets, and implement modern project management tools (ClickUp, Asana, Monday). We build the digital infrastructure that allows a remote team to plug in and be productive from Day 1. We believe you should never pay a human to do what a robot can do for free."
            },
            {
                type: 'benefits',
                heading: "Transformation Outcomes",
                items: [
                    { icon: <Cpu size={24} />, title: "Workflow Automation", desc: "We use Zapier/Make to connect your apps, eliminating manual data transfer." },
                    { icon: <TrendingUp size={24} />, title: "Data Centralization", desc: "Move from scattered Excel sheets to a unified Cloud CRM/Database source of truth." },
                    { icon: <Shield size={24} />, title: "Security Protocols", desc: "Implement LastPass/1Password and 2FA, securing your access points before sharing credentials." },
                    { icon: <Zap size={24} />, title: "Async Communication", desc: "Setup Slack/Teams channels and protocols to reduce meetings by 40%." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Our Modernization Toolkit",
                sections: [
                    {
                        title: "The 'No-Code' Revolution",
                        content: "You don't need a $50k custom software build. We utilize powerful no-code tools like Airtable and Softr to build custom internal portals for your operations. This gives you enterprise-grade software at startup prices."
                    },
                    {
                        title: "CRM Implementation",
                        content: "Your customer data is your gold mine. We clean, migrate, and structure your HubSpot, Salesforce, or Pipedrive instance to ensure that when your offshore sales support team starts, they have clean data to work with."
                    },
                    {
                        title: "SOP Digitization",
                        content: "Static PDFs get ignored. We build interactive SOPs using tools like Loom and Guru. We create a searchable, video-first training library that enables self-serve onboarding for new hires."
                    }
                ]
            },
            {
                type: 'education',
                heading: "The 'Digital Twin' Concept",
                text: "Before we hire a single person, we aim to build a 'Digital Twin' of your operation. This means every process has a digital trigger and action. \n\nFor example, instead of emailing a task to an employee, a form submission in your website triggers a notification in Slack, creates a card in Trello, and auto-populates a draft email in Gmail. The human only steps in to review and hit send. This dramatically lowers the skill floor required for the role, allowing you to hire for attitude and reliability rather than obscure technical experience."
            },
            {
                type: 'cta',
                heading: "Audit Your Tech Stack",
                text: "Is your business running on 'digital duct tape'? Let's clean it up before we scale it up.",
                button: "Get Free Tech Audit"
            }
        ]
    }
};

const Services = () => {
    const { serviceId } = useParams();
    // Default to a landing page if no ID (or handle /services root)
    const service = servicesData[serviceId];

    if (!serviceId) {
        // Optional: Render a Services Landing Page listing all services
        return <Navigate to="/services/ai-staffing" replace />;
    }

    if (!service) {
        return <Navigate to="/404" replace />;
    }

    return (
        <div className="bg-white">
            <SEO
                title={service.title}
                description={service.metaDesc}
                pageKey={SEO_KEYS[serviceId] || `service_${serviceId}`}
            />
            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#20222D] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src={service.hero.image} alt={service.hero.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-[#20222D] mix-blend-multiply"></div>
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            {service.hero.title}
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            {service.hero.subtitle}
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Dynamic Content Rendering */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                {service.content.map((block, index) => {
                    switch (block.type) {
                        case 'intro':
                        case 'education':
                            return (
                                <FadeIn key={index} className="mb-16">
                                    <h2 className="text-3xl font-bold text-[#20222D] mb-6">{block.heading}</h2>
                                    <div className="prose prose-lg break-words text-gray-600 leading-relaxed whitespace-pre-line">
                                        {block.text}
                                    </div>
                                </FadeIn>
                            );
                        case 'benefits':
                            return (
                                <FadeIn key={index} className="mb-16 bg-gray-50 p-8 rounded-2xl border border-gray-100">
                                    <h2 className="text-3xl font-bold text-[#20222D] mb-8 text-center">{block.heading}</h2>
                                    <div className="grid md:grid-cols-2 gap-8">
                                        {block.items.map((item, i) => (
                                            <div key={i} className="flex items-start">
                                                <div className="text-[#A2CB52] bg-white p-3 rounded-lg shadow-sm mr-4">
                                                    {item.icon}
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-[#20222D] text-lg mb-2">{item.title}</h3>
                                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </FadeIn>
                            );
                        case 'deepDive':
                            return (
                                <FadeIn key={index} className="mb-16">
                                    <h2 className="text-3xl font-bold text-[#20222D] mb-8">{block.heading}</h2>
                                    <div className="space-y-8">
                                        {block.sections.map((sec, i) => (
                                            <div key={i} className="border-l-4 border-[#A2CB52] pl-6 py-2">
                                                <h3 className="text-xl font-bold text-[#20222D] mb-2">{sec.title}</h3>
                                                <p className="text-gray-600 leading-relaxed">{sec.content}</p>
                                            </div>
                                        ))}
                                    </div>
                                </FadeIn>
                            );
                        case 'cta':
                            return (
                                <FadeIn key={index} className="mt-20 text-center bg-[#20222D] rounded-3xl p-12 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                                    <h2 className="text-3xl font-bold text-white mb-6">{block.heading}</h2>
                                    <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">{block.text}</p>
                                    <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                                        {block.button || "Contact Us"}
                                        <ArrowRight className="ml-2" size={20} />
                                    </Link>
                                </FadeIn>
                            );
                        default:
                            return null;
                    }
                })}
            </div>
        </div>
    );
};

export default Services;
